import Mustache from 'mustache'

import MESSAGES from 'core/data-model/constants/validation-messages'
import { asYears } from 'core/service/insured/age'
import { formatNumber } from 'core/service/lib/number-format'

export const _validateLoanAmount = (rule, loanAmount) => {
  const { minimumValue } = rule
  const isLoanAmountAtLeastEqualMinValue = loanAmount >= minimumValue

  return isLoanAmountAtLeastEqualMinValue
    ? { isValid: true }
    : {
        isValid: false,
        message: Mustache.render(MESSAGES.RULE_LOAN_AMOUNT, { minimumValue: formatNumber(minimumValue) }),
      }
}

export const _validateLoanTerm = (rule, loanTerm, insuredAge) => {
  const { minLoanTerm, maxLoanTerm, maxInsuredAgeAtEndOfLoanTerm } = rule
  const isLoanTermLessThanMinValue = loanTerm < minLoanTerm
  const isLoanTermGreaterThanMaxValue = loanTerm > maxLoanTerm

  const insuredAgeAtEndOfLoanTerm = loanTerm + asYears(insuredAge)
  const isInsuredAgeAtEndOfLoanTermGreaterThanMaxValue = insuredAgeAtEndOfLoanTerm > maxInsuredAgeAtEndOfLoanTerm

  if (isLoanTermLessThanMinValue || isLoanTermGreaterThanMaxValue || isInsuredAgeAtEndOfLoanTermGreaterThanMaxValue) {
    return {
      isValid: false,
      message: Mustache.render(MESSAGES.RULE_LOAN_TERM, {
        minLoanTerm: formatNumber(minLoanTerm),
        maxLoanTerm: formatNumber(maxLoanTerm),
        maxInsuredAgeAtEndOfLoanTerm: formatNumber(maxInsuredAgeAtEndOfLoanTerm),
      }),
    }
  }

  return { isValid: true }
}

export const _validateSumAssured = (rule, sumAssured, loanAmount) => {
  const { minimumValue } = rule
  const isSumAssuredGreaterThanEqualMinValue = sumAssured >= minimumValue
  const isSumAssuredLessThanEqualToLoanAmount = sumAssured <= loanAmount

  if (isSumAssuredGreaterThanEqualMinValue && isSumAssuredLessThanEqualToLoanAmount) {
    return { isValid: true }
  }

  return {
    isValid: false,
    message: Mustache.render(MESSAGES.RULE_LOAN_SUM_ASSURED, { minimumValue: formatNumber(minimumValue) }),
  }
}

export const _validateCoverageTerm = (rule, coverageTerm, loanTerm) => {
  const { minCoverageTerm, maxCoverageTerm } = rule
  const isCoverageTermLessThanMinValue = Number(coverageTerm) < Number(minCoverageTerm)
  const isCoverageTermGreaterThanMaxValue = Number(coverageTerm) > Number(maxCoverageTerm)
  const isCoverageTermGreaterThanLoanTerm = Number(coverageTerm) > Number(loanTerm)
  if (isCoverageTermLessThanMinValue || isCoverageTermGreaterThanMaxValue || isCoverageTermGreaterThanLoanTerm) {
    return {
      isValid: false,
      message: Mustache.render(MESSAGES.RULE_COVERAGE_PERIOD_INSURED, {
        minCoverageTerm: formatNumber(minCoverageTerm),
      }),
    }
  }

  return { isValid: true }
}

/**
 * createBasicPlanRuleValidation
 * @param {ReturnType<import('core/data-model/basic-plan/repository').createBasicPlanRepository>} basicPlanRepository
 */
export function createBasicPlanRuleValidation(basicPlanRepository) {
  const validateLoanAmount = async (productCode, loanAmount) => {
    const rule = await basicPlanRepository.getBasicPlanRuleByType(productCode, 'LoanAmountRule')
    return _validateLoanAmount(rule, loanAmount)
  }

  const validateLoanTerm = (productCode, loanTerm, insuredAge) =>
    basicPlanRepository
      .getBasicPlanRuleByType(productCode, 'LoanTermRule')
      .then((rule) => _validateLoanTerm(rule, loanTerm, insuredAge))

  const validateSumAssured = async (productCode, sumAssured, loanAmount) => {
    const rule = await basicPlanRepository.getBasicPlanRuleByType(productCode, 'SumAssuredRule')
    return _validateSumAssured(rule, sumAssured, loanAmount)
  }

  const validateCoverageTerm = (productCode, coverageTerm, loanTerm) =>
    basicPlanRepository
      .getBasicPlanRuleByType(productCode, 'CoverageTermRule')
      .then((rule) => _validateCoverageTerm(rule, coverageTerm, loanTerm))

  return {
    validateLoanAmount,
    validateLoanTerm,
    validateSumAssured,
    validateCoverageTerm,
  }
}
